<template>
  <screening-workflow-panel
    :review="review"
    :current-researcher="currentResearcher"
    :pending-connection.sync="
      referencesForScreeningConflictReviewConnection
    "
    :pending-connection-query="
      $apollo.queries.referencesForScreeningConflictReviewConnection
    "
    :history-connection="
      referencesByScreeningConflictResolutionHistoryConnection
    "
    :history-connection-query="
      $apollo.queries
        .referencesByScreeningConflictResolutionHistoryConnection
    "
    :search-input.sync="searchInput"
    :revoke-vote-func="revokeOutcome"
    :get-vote-by-current-user-func="getResolutionByCurrentUser"
    :filtering-input="filteringInput"
    @clear-filtering-input="$emit('clear-filtering-input')"
  >
    <template #empty-state>
      <screen-tab-conflicting-empty-state />
    </template>
  </screening-workflow-panel>
</template>

<script>
import ScreeningWorkflowPanel from './ScreeningWorkflowPanel';
import ScreenTabConflictingEmptyState from './ScreenTabConflictingEmptyState';
import {
  GetReferencesForScreeningConflictReview,
  GetReferencesByScreeningConflictResolutionHistory,
} from '@/graphql';
import {
  Review,
  Researcher,
  ReferenceOffsetConnection,
  ReferenceFilteringInputDto,
} from '@/models';
import { mapActions } from 'vuex';
import { isEqual } from 'lodash';

const PAGE_SIZE = 50;

export default {
  name: 'ScreenTabConflicting',
  components: {
    ScreeningWorkflowPanel,
    ScreenTabConflictingEmptyState,
  },
  props: {
    review: {
      type: Review,
      required: true,
    },
    currentResearcher: {
      type: Researcher,
      required: true,
    },
    filteringInput: {
      type: ReferenceFilteringInputDto,
      required: true,
    },
    active: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['clear-filtering-input'],
  data: () => ({
    searchInput: null,
    referencesForScreeningConflictReviewConnection: null,
    referencesByScreeningConflictResolutionHistoryConnection: null,
  }),
  apollo: {
    referencesForScreeningConflictReviewConnection: {
      query: GetReferencesForScreeningConflictReview,
      variables() {
        return {
          reviewId: this.review.id,
          limit: PAGE_SIZE,
          searchQuery: this.searchInput || null,
          hasTag: this.filteringInput.hasTag,
          hasKeyword: this.filteringInput.hasKeyword,
          hasAuthor: this.filteringInput.hasAuthor,
          hasPlacePublished: this.filteringInput.hasPlacePublished,
        };
      },
      update: (data) =>
        new ReferenceOffsetConnection(
          data.referencesForScreeningConflictReview
        ),
      skip() {
        return !this.review?.id;
      },
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    },
    referencesByScreeningConflictResolutionHistoryConnection: {
      query: GetReferencesByScreeningConflictResolutionHistory,
      variables() {
        return {
          reviewId: this.review.id,
          limit: PAGE_SIZE,
        };
      },
      update: (data) =>
        new ReferenceOffsetConnection(
          data.referencesByScreeningConflictResolutionHistory
        ),
      skip() {
        return (
          !this.review?.id ||
          !this.referencesForScreeningConflictReviewConnection
        );
      },
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    },
  },
  watch: {
    active(active) {
      if (
        active &&
        this.referencesForScreeningConflictReviewConnection
      ) {
        this.$apollo.queries.referencesForScreeningConflictReviewConnection.refetch();
        this.$apollo.queries.referencesByScreeningConflictResolutionHistoryConnection.refetch();
      }
    },
    filteringInput(newInput, oldInput) {
      // Clear the cache when the filtering input changes to show the loading state.
      const changed = !isEqual(newInput, oldInput);
      if (this.active && changed) {
        this.referencesForScreeningConflictReviewConnection = null;
      }
    },
  },
  methods: {
    ...mapActions({
      revokeOutcome: 'screeningOutcome/removeConflictResolution',
    }),
    getResolutionByCurrentUser(reference) {
      return (
        reference?.screeningOutcome?.resolvedBy?.researcher.id ===
          this.currentResearcher.id && reference?.screeningOutcome
      );
    },
  },
};
</script>
